import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  const currentCategoryName = currentCategory?.name ?? slug ?? '';

  useEffect(() => {
    document.title = `${currentCategoryName} - 911爆料网`;
  }, [currentCategoryName]);

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        {/* Breadcrumb */}
        <nav aria-label="面包屑导航" className="w-full px-4 md:px-6 lg:px-10 pt-6 pb-2">
          <ol className="flex items-center gap-2 text-xs text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line text-xs"></i>
            </li>
            <li>
              <span className="text-foreground-800 font-medium">{currentCategoryName}</span>
            </li>
          </ol>
        </nav>

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12">
          <div className="flex items-center gap-4 mb-4">
            {currentCategory && (
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className={`${currentCategory.icon} text-xl`}></i>
              </div>
            )}
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-950">
                {currentCategoryName}
              </h1>
              {currentCategory && (
                <p className="text-sm text-foreground-500 mt-1">{currentCategory.description}</p>
              )}
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 lg:px-10 pb-6">
          <nav aria-label="分类切换" className="flex flex-wrap items-center gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </nav>
        </section>

        {/* Article List */}
        <section className="w-full px-4 md:px-6 lg:px-10 pb-20">
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <article
                  key={article.id}
                  className="bg-background-50 rounded-lg overflow-hidden group cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                >
                  <Link to={`/article/${article.slug}`} className="block">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={article.cardImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-accent-500 text-white text-xs font-medium rounded-full">
                          {currentCategoryName}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 md:p-5">
                      <h2 className="text-base md:text-lg font-medium text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.publishDate}</span>
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {(article.readCount / 10000).toFixed(1)}万
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-chat-3-line"></i>
                            {article.commentCount}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <h3 className="text-lg font-medium text-foreground-700 mb-2">暂无文章</h3>
              <p className="text-sm text-foreground-500">该分类下暂时没有文章，请查看其他分类</p>
              <Link
                to="/"
                className="inline-block mt-4 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          )}
        </section>

        {/* SEO Schema.org */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `${currentCategoryName} - 911爆料网`,
            description: currentCategory?.description ?? `${currentCategoryName}最新资讯`,
            url: `https://www.911baoliao.com/category/${slug}`,
            inLanguage: 'zh-CN',
            isPartOf: {
              '@type': 'WebSite',
              name: '911爆料网',
              url: 'https://www.911baoliao.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.911baoliao.com' },
                { '@type': 'ListItem', position: 2, name: currentCategoryName, item: `https://www.911baoliao.com/category/${slug}` },
              ],
            },
          })}
        </script>
      </main>
      <Footer />
    </>
  );
}